import React, { useEffect, useState } from 'react';
import { Spinner, useSimpleSpinner } from '../spinner/index';
import { useAuditoria } from '../../hooks/useAuditoria';
import SelectDropdown from '../selectDropdown';
import { Modal, ClausulasModalContent, RechazoConfirmacionContent, ModalCorreoTemporal, ResultadoGestionModal } from '../Modal';
import { GestionCups } from '../GestionCups';
import { contratosFixture, actualizarEstadoAnexoFixture } from '../../fixtures';
import { ContrastModeToggle } from '../ContrastModeToggle/ContrastModeToggle';
import { esCorreoValido } from '../../utils/emailValidation';
import AnexoIcon from '../Icons/Anexo.icon';
import { FEATURE_NOTIFICACION_CORREO_HABILITADA } from '../../featureFlags';

import '../../app/app.module.css';
import './busqueda.css';
import '../Resultados/resultados.css';

interface UserInfo {
  LegalID?: string;
  Nit?: string;
  [key: string]: unknown;
}

interface Anexo {
  Id: number;
  Nombre: string;
  FechaFin: string;
  FechaInicio: string;
  OtrosAspectos: string;
  TipoAcuerdo: string;
}

interface Contrato {
  Id: number;
  Nombre: string;
  Anexos: Anexo[];
}

interface AcuerdosTarifariosResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    Contratos: Contrato[];
    HayAcuerdos: boolean;
  };
}

interface ActualizarEstadoAnexoResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    __type: string;
    Code: number;
    Description: string;
    Result: string;
    EstadoNotificacion?: string;
    MensajeNotificacion?: string;
    CorreoDestino?: string;
  };
}


interface ClaimsResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    __type: string;
    EsIndependiente: boolean;
    Givenname: string;
    Proxy: string | null;
    UserId: string | null;
    UserTypeId: string | null;
    emailaddress: string;
    role: string[];
  };
}

interface ProxyData {
  ciamId: string;
  firstName: string;
  middleName?: string;
  paternalSurname: string;
  maternalSurname: string;
  documents: Array<{
    documentShortName: string;
    countryCode: string;
    documentNumber: string;
  }>;
}

interface AprobadorInfo {
  nombre: string;
  correo: string;
  ip: string;
  userId: string;
  userTypeId: string;
  roles: string;
  sede: string;
}

const hasMeaningfulContent = (html?: string): boolean => {
  if (!html) return false;

  const plainText = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return plainText.length > 0;
};

const Busqueda: React.FC = () => {
  const { loading, startLoading, stopLoading } = useSimpleSpinner();
  const { registrarAuditoria } = useAuditoria();
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [contratoSeleccionado, setContratoSeleccionado] = useState<number | null>(null);
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [anexoSeleccionado, setAnexoSeleccionado] = useState<number | null>(null);
  const [mostrarTabla, setMostrarTabla] = useState(false);
  const [error, setError] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isAuthenticatedBoolean, setIsAuthenticatedBoolean] = useState(false);
  
  // Estados para gestión de CUPS
  const [mostrarGestionCups, setMostrarGestionCups] = useState(false);
  
  // Estados para el modal
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [clausulasAceptadas, setClausulasAceptadas] = useState(false);
  
  // Estados para el modal PDF - REMOVIDOS (ahora se descarga directamente)
  
  // Estados para el modal de confirmación
  const [modalStep, setModalStep] = useState<'clausulas' | 'confirmar-rechazo' | 'confirmar-aprobacion' | 'exito' | 'error'>('clausulas');
  const [, setMensajeExito] = useState('');
  const [mensajeError, setMensajeError] = useState('');
  const [procesandoOperacion, setProcesandoOperacion] = useState(false);
  
  // Estado para guardar el correo destino de la notificación y detalles del resultado
  const [correoDestino, setCorreoDestino] = useState('');
  const [tipoResultadoOperacion, setTipoResultadoOperacion] = useState<'aprobado' | 'rechazado'>('aprobado');
  const [estadoNotificacionRespuesta, setEstadoNotificacionRespuesta] = useState<string | undefined>();
  const [mensajeNotificacionRespuesta, setMensajeNotificacionRespuesta] = useState<string | undefined>();

  // Nuevo estado para información del aprobador
  const [aprobadorInfo, setAprobadorInfo] = useState<AprobadorInfo | null>(null);
  
  // Estado para capturar correo temporal si está vacío
  const [correoTemporal, setCorreoTemporal] = useState('');
  const [mostrarModalCorreoTemporal, setMostrarModalCorreoTemporal] = useState(false);
  const [tipoOperacionPendiente, setTipoOperacionPendiente] = useState<'aprobacion' | 'rechazo' | null>(null);
  const [motivoRechazoTemporal, setMotivoRechazoTemporal] = useState('');
  
  // Estado para controlar descarga de PDF
  const [descargandoPdf, setDescargandoPdf] = useState(false);

  useEffect(() => {
    const sharePointElement = document.querySelector('[data-name="Campo de página: Contenido de la página"]');
    if (sharePointElement) {
      sharePointElement.remove();
    }
  }, []);

  useEffect(() => {
    isAuthenticated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAuthenticated = () => {
    const authenticatedUser = compTools.obtenerInformacionUsuario();
    setUserInfo(authenticatedUser);
    setIsAuthenticatedBoolean(true);
    fetchContratos(authenticatedUser);
    // Obtener información del aprobador al inicio
    fetchAprobadorInfo();
  };

  const fetchContratos = async (userData: UserInfo) => {
    startLoading();
    try {
      // Si estamos en modo desarrollo, usamos el fixture
      if (__DEV__ || import.meta.env.MODE === 'development') {
        // Simulamos un pequeño delay para simular la llamada al API
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const data: AcuerdosTarifariosResponse = contratosFixture;
        
        if (data.ResponseCode === 200 && data.Result.Contratos) {
          setContratos(data.Result.Contratos);
        } else {
          setError(true);
        }
        
        stopLoading();
        return;
      }

      let nit;
      
      // En producción, siempre usamos el dato del userData
      // Verificamos si es una empresa (TipoIdentificacion === 2)
      if (userData?.LegalID) {
        nit = userData.LegalID;
        //nit = '41675348';

      } else {
        // Si no es empresa o no tiene LegalID, manejamos el caso de error
        console.error('Error: No se pudo obtener el NIT del usuario.');
        setError(true);
        stopLoading();
        return; // Salimos de la función para evitar la llamada al API sin NIT
      }
      
      const response = await fetch(
        `/_vti_bin/Servicios/ServicioTransar.svc/ObtenerAcuerdosTarifarios?nit=${nit}&estado=1`
      );
      const data: AcuerdosTarifariosResponse = await response.json();
      
      if (data.ResponseCode === 200 && data.Result.Contratos) {
        setContratos(data.Result.Contratos);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error al cargar contratos:', err);
      setError(true);
    } finally {
      stopLoading();
    }
  };

  const fetchAprobadorInfo = async () => {
    try {
      // Si estamos en modo desarrollo, usamos datos dummy
      if (__DEV__ || import.meta.env.MODE === 'development') {
        const dummyAprobador = {
          nombre: "EMANUEL ALCIDES CORRAL ROMO",
          correo: "dummy@correo.com",
          ip: "192.168.1.1",
          userId: "0",
          userTypeId: "0",
          roles: 'N/A',
          sede: 'Sede no asociada',
        };
        
        setAprobadorInfo(dummyAprobador);
        
        // Persister la información en localStorage para que esté disponible para la gestión automática
        localStorage.setItem('aprobador-info', JSON.stringify(dummyAprobador));
        return;
      }

      const response = await fetch(
        '/_vti_bin/Servicios/ServicioTransar.svc/ConsultarInformacionClaims'
      );
      const data: ClaimsResponse = await response.json();
      
      if (data.ResponseCode === 200 && data.Result) {
        const userInfo = compTools.obtenerInformacionUsuario();
        const userIdEfectivo = (data.Result.UserId ?? '').toString().trim() || (userInfo?.LegalID ?? '').toString();
        const userTypeIdEfectivo = (data.Result.UserTypeId ?? '').toString().trim() || (userInfo?.TipoIdentificacion ?? '').toString();

        let nombreAprobador = '';
        
        // Verificar si hay datos en proxy (delegado)
        if (data.Result.Proxy) {
          try {
            const proxyData: ProxyData = JSON.parse(data.Result.Proxy);
            // Construir nombre completo del delegado
            nombreAprobador = `${proxyData.firstName} ${proxyData.middleName || ''} ${proxyData.paternalSurname} ${proxyData.maternalSurname}`.trim();
          } catch (error) {
            console.error('Error al parsear proxy data:', error);
            // Fallback al Givenname si no se puede parsear proxy
            nombreAprobador = data.Result.Givenname;
          }
        } else {
          // Es empresa, usar Givenname
          nombreAprobador = data.Result.Givenname;
        }

        // Extraer roles del usuario (igual que referencia: filtrar los que contienen 'salud')
        const rolesRaw = data.Result.role || [];
        const rolesFiltered = (Array.isArray(rolesRaw) ? rolesRaw : [rolesRaw])
          .filter((item) => item && item.toLowerCase().includes('salud'));
        const rolesText = rolesFiltered.length > 0 ? rolesFiltered.join(', ') : 'N/A';

        // Obtener sede desde CheckUserAppointmentsAccessAPI (igual que referencia)
        let sedeText = 'Sede no asociada';
        try {
          const sedeResponse = await fetch(
            `/_vti_bin/Servicios/ServicioTransar.svc/CheckUserAppointmentsAccessAPI?userTypeId=${userTypeIdEfectivo}&userId=${userIdEfectivo}`
          );
          const sedeData = await sedeResponse.json();
          if (sedeData?.Result?.PBSLocation &&
              sedeData.Result.PBSLocation !== '' &&
              sedeData.Result.PBSLocation !== 'N/A') {
            sedeText = sedeData.Result.PBSLocation;
          }
        } catch {
          // fallback: 'Sede no asociada'
        }

        const aprobadorData = {
          nombre: nombreAprobador,
          correo: data.Result.emailaddress || "",
          ip: "192.168.1.1", // IP dummy por ahora
          userId: userIdEfectivo,
          userTypeId: userTypeIdEfectivo,
          roles: rolesText,
          sede: sedeText,
        };

        setAprobadorInfo(aprobadorData);
        
        // Persister la información en localStorage para que esté disponible para la gestión automática
        localStorage.setItem('aprobador-info', JSON.stringify(aprobadorData));
      } else {
        console.error('Error al obtener información del aprobador:', data.ResponseDetail);
        // No setear aprobadorInfo - bloquear el flujo
        console.error('FLUJO BLOQUEADO: No se pudo obtener información del usuario aprobador');
      }
    } catch (error) {
      console.error('Error en la llamada al servicio de claims:', error);
      // No setear aprobadorInfo - bloquear el flujo  
      console.error('FLUJO BLOQUEADO: Error de conexión al servicio de claims');
    }
  };

  const handleContratoChange = (value: string | number, data?: Record<string, unknown>) => {
    const contratoId = Number(value);
    setContratoSeleccionado(contratoId);
    
    const contrato = contratos.find(c => c.Id === contratoId);
    setAnexos(contrato ? contrato.Anexos : []);
    setAnexoSeleccionado(null);
    setMostrarTabla(false);
    setMostrarGestionCups(false);
  };

  const handleAnexoChange = (value: string | number, data?: Record<string, unknown>) => {
    const anexoId = Number(value);
    setAnexoSeleccionado(anexoId);
    setMostrarGestionCups(false);
    
    if (anexoId) {
      // Obtener datos del anexo seleccionado
      const anexoData = anexos.find(a => a.Id === anexoId);
      
      // Mostramos los datos del anexo seleccionado
      setMostrarTabla(true);
      
      // Si no hay OtrosAspectos, ir directamente a gestión de CUPS
      if (!hasMeaningfulContent(anexoData?.OtrosAspectos)) {
        // Pequeño delay para que se renderice la tabla antes de mostrar gestión CUPS
        setTimeout(() => {
          setMostrarGestionCups(true);
        }, 100);
      }
      
      // Hacer scroll hacia abajo para enfocar la sección de resultados después de un pequeño delay
      setTimeout(() => {
        try {
          // Buscar el hr que separa los formularios de los resultados
          const hrElement = document.querySelector('.separador-resultados');
          if (hrElement) {
            const rect = hrElement.getBoundingClientRect();
            const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
            const targetY = currentScrollY + rect.top - 90;
            
            window.scrollTo({
              top: targetY,
              behavior: 'smooth'
            });
          } else {
            // Fallback: scroll hacia la tabla si no se encuentra el hr
            const tablaElement = document.querySelector('#resultadoImagenesDiagnosticas');
            if (tablaElement) {
              tablaElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
            }
          }
        } catch (error) {
          console.warn('Error al hacer scroll a la sección de resultados:', error);
        }
      }, 100); // Pequeño delay para que la tabla se renderice
    } else {
      setMostrarTabla(false);
    }
  };

  
  const DescargarAnexoPdf = async () => {
  if (!contratoSeleccionado || !anexoSeleccionado || !userInfo) {
    console.error('Faltan datos para descargar el PDF');
    return;
  }

  const contratoData = contratos.find(c => c.Id === contratoSeleccionado);
  const anexoData = anexos.find(a => a.Id === anexoSeleccionado);

  if (!contratoData || !anexoData) {
    console.error('No se encontraron los datos del contrato o anexo');
    return;
  }

  setDescargandoPdf(true);

  try {
    const requestBody = {
      Nit: (userInfo.LegalID || userInfo.Nit || '').toString(),
      Contrato: contratoData.Nombre,
      Anexo: anexoData.Nombre,
      TipoDocumento: "ACUERDO TARIFARIO"
    };

    const response = await fetch(
      '/_vti_bin/Servicios/ServicioTransar.svc/ObtenerDocumentoAnexoTarifario',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.ResponseCode === 200 && data.Result && data.Result.Base64) {
      // Método OPTIMIZADO: usar fetch con data URI para crear el Blob
      // Esto es mucho más rápido que atob + loop manual
      const blobResponse = await fetch(`data:application/pdf;base64,${data.Result.Base64}`);
      const blob = await blobResponse.blob();

      // Crear URL y descargar
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = data.Result.NomDoc || `${anexoData?.Nombre || 'documento'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Liberar el objeto URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } else {
      console.error('Error al obtener el documento:', data.ResponseDetail);
      alert('Error al descargar el documento PDF. Por favor, intente nuevamente.');
    }
  } catch (err) {
    console.error('Error al descargar PDF:', err);
    alert('Error al descargar el documento PDF. Por favor, intente nuevamente.');
  } finally {
    setDescargandoPdf(false);
  }
};


  const consultarClausulas = () => {
    const anexoData = anexos.find(a => a.Id === anexoSeleccionado);

    if (!hasMeaningfulContent(anexoData?.OtrosAspectos)) {
      setMostrarGestionCups(true);
      return;
    }

    const mensaje = `${anexoData?.OtrosAspectos || 'No hay información de cláusulas disponible.'}`;
    
    // Asegurar que tenemos un mensaje limpio de cláusulas (no de errores)
    setModalMessage(mensaje);
    setModalStep('clausulas'); // Forzar el paso a clausulas
    setMensajeError(''); // Limpiar cualquier error anterior
    setShowModal(true);
  };

  const cerrarModal = () => {
    // Prevenir cerrar el modal si está en estado de éxito (los errores sí se pueden cerrar)
    if (modalStep === 'exito') return;
    
    setShowModal(false);
    setClausulasAceptadas(false);
    setModalStep('clausulas');
    setMensajeExito('');
    setMensajeError('');
    // Importante: NO limpiar modalMessage aquí para conservar las cláusulas
  };

  const reiniciarConsulta = () => {
    // Reiniciar todos los estados a la consulta inicial
    setContratoSeleccionado(null);
    setAnexoSeleccionado(null);
    setAnexos([]);
    setMostrarTabla(false);
    setClausulasAceptadas(false);
    setModalStep('clausulas');
    setMensajeExito('');
    setMensajeError('');
    setMostrarGestionCups(false);
    
    // Hacer scroll hacia arriba de la página (más arriba)
    try {
      // Scroll al inicio absoluto de la página para mejor visibilidad
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (error) {
      // Fallback en caso de error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // Recargar los contratos
    if (userInfo) {
      fetchContratos(userInfo);
    }
  };

  const finalizarOperacionExitosa = () => {
    setShowModal(false);
    reiniciarConsulta();
  };

  // cerrarPdfModal removido - ya no se usa modal para PDF

  const cancelarConfirmacion = () => {
    // Volver al paso de clausulas y limpiar errores
    setModalStep('clausulas');
    setMensajeError('');
  };

  // Validar correo y mostrar modal temporal si es necesario
  const validarYContinuarConOperacion = (operacion: 'aprobacion' | 'rechazo', motivoRechazo?: string) => {
    const correoDisponible = (aprobadorInfo?.correo || '').trim();

    if (FEATURE_NOTIFICACION_CORREO_HABILITADA && !esCorreoValido(correoDisponible)) {
      // Correo vacío o inválido - ocultar modal principal y mostrar modal para solicitar correo temporal
      setTipoOperacionPendiente(operacion);
      if (motivoRechazo) setMotivoRechazoTemporal(motivoRechazo);
      setShowModal(false);
      setMostrarModalCorreoTemporal(true);
      console.log('[EMAIL-VALIDATION] Correo vacío o inválido, mostrando modal temporal');
      return;
    }

    // Correo disponible y válido - continuar con la operación
    console.log('[EMAIL-VALIDATION] Correo válido disponible:', correoDisponible);
    if (operacion === 'aprobacion') {
      confirmarAprobacionFinal();
    } else {
      confirmarRechazoFinal(motivoRechazo || '');
    }
  };

  const procedeConCorreoTemporal = async (correoLimpio: string) => {
    // Actualizar aprobadorInfo con el correo temporal
    if (aprobadorInfo) {
      const aprobadorActualizado = {
        ...aprobadorInfo,
        correo: correoLimpio
      };
      setAprobadorInfo(aprobadorActualizado);
      localStorage.setItem('aprobador-info', JSON.stringify(aprobadorActualizado));
    }
    
    // Ocultar modal de correo temporal y reabrir modal principal
    setMostrarModalCorreoTemporal(false);
    setShowModal(true);
    
    // Ejecutar operación pasando el correo ingresado directamente
    const operacion = tipoOperacionPendiente;
    const motivo = motivoRechazoTemporal;

    // Limpiar campos temporales
    setCorreoTemporal('');
    setTipoOperacionPendiente(null);
    setMotivoRechazoTemporal('');

    if (operacion === 'aprobacion') {
      await confirmarAprobacionFinal(correoLimpio);
    } else if (operacion === 'rechazo') {
      await confirmarRechazoFinal(motivo, correoLimpio);
    }
  };

  const ciudadUsuarioAuditoria =
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('CiudadUsuario') : null) || 'N/A';

  const actualizarEstadoAnexo = async (
    estado: "4" | "0", 
    observaciones?: string,
    correoOverride?: string
  ): Promise<{
    exito: boolean;
    mensaje?: string;
    correoDestino?: string;
    estadoNotificacion?: string;
    mensajeNotificacion?: string;
  }> => {
    try {
      // Validar que tengamos un anexo seleccionado
      if (!anexoSeleccionado) {
        console.error('No hay anexo seleccionado');
        return { exito: false, mensaje: 'No hay anexo seleccionado' };
      }

      // Validar que tengamos información del aprobador
      if (!aprobadorInfo) {
        return { exito: false, mensaje: 'No se ha cargado la información del aprobador' };
      }

      // Determinar correo a usar (priorizando override directo del modal)
      const correoAUsar = (correoOverride || aprobadorInfo.correo || '').trim();
      if (FEATURE_NOTIFICACION_CORREO_HABILITADA && !esCorreoValido(correoAUsar)) {
        console.warn('[EMAIL-VALIDATION] CorreoAprobador vacío o inválido detectado en actualizarEstadoAnexo. Solicitando correo temporal.');
        setTipoOperacionPendiente(estado === "4" ? 'aprobacion' : 'rechazo');
        if (observaciones) setMotivoRechazoTemporal(observaciones);
        setShowModal(false);
        setMostrarModalCorreoTemporal(true);
        return { exito: false, mensaje: 'Debe ingresar un correo electrónico válido para continuar.' };
      }


      // Si estamos en modo desarrollo, usamos el fixture
      if (__DEV__ || import.meta.env.MODE === 'development') {
        // Simulamos un pequeño delay para simular la llamada al API
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const data: ActualizarEstadoAnexoResponse = actualizarEstadoAnexoFixture;
        return { 
          exito: data.ResponseCode === 200 && data.Result?.Result === "OK", 
          correoDestino: correoAUsar,
          estadoNotificacion: 'EXITOSA',
          mensajeNotificacion: 'Notificación enviada correctamente.'
        };
      }

      const body: { 
        AnexoId: number; 
        Estado: string; 
        Observaciones?: string;
        NombreAprobador: string;
        CorreoAprobador: string;
        IpAprobador: string;
        EstadoCups: string;
        ActualizarCupsMasivo?: boolean;
        NombreAnexoCorreo?: string;
        CodigoAnexoCorreo?: string;
        TestMode?: boolean;
        EnviarNotificacionCorreo?: boolean;
      } = {
        AnexoId: anexoSeleccionado,
        Estado: estado,
        NombreAprobador: aprobadorInfo.nombre,
        CorreoAprobador: correoAUsar,
        IpAprobador: aprobadorInfo.ip,
        EstadoCups: estado === "4" ? "SI" : "NO", // "SI" para aprobación (4), "NO" para rechazo (5)
        ActualizarCupsMasivo: false,
        TestMode: false,
        EnviarNotificacionCorreo: FEATURE_NOTIFICACION_CORREO_HABILITADA
      };

      if (anexoSeleccionadoData?.Nombre) {
        body.NombreAnexoCorreo = anexoSeleccionadoData.Nombre;
      }

      body.CodigoAnexoCorreo = anexoSeleccionado.toString();

      // Solo incluir observaciones para rechazo (estado "5")
      if (estado === "0" && observaciones) {
        body.Observaciones = observaciones;
      }

      const response = await fetch(
        '/_vti_bin/Servicios/ServicioTransar.svc/ActualizarEstadoAnexotarifario',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body)
        }
      );
      
      // LOG para verificar exactamente qué se envió
      console.log('[PAYLOAD-ENVIADO]', JSON.stringify(body, null, 2));
      
      const data: ActualizarEstadoAnexoResponse = await response.json();
      const esExitoso = data.ResponseCode === 200 && data.Result && data.Result.Result === "OK";
      
      return { 
        exito: esExitoso,
        correoDestino: data.Result?.CorreoDestino || correoAUsar,
        estadoNotificacion: data.Result?.EstadoNotificacion,
        mensajeNotificacion: data.Result?.MensajeNotificacion,
        mensaje: esExitoso ? undefined : data.ResponseDetail || 'Error desconocido del servicio'
      };
    } catch (error) {
      console.error('Error en la llamada al servicio:', error);
      return { 
        exito: false, 
        mensaje: 'Error de conexión con el servicio' 
      };
    }
  };

  const confirmarRechazoFinal = async (motivo: string, correoOverride?: string) => {
    setProcesandoOperacion(true);

    try {
      const resultado = await actualizarEstadoAnexo("0", motivo, correoOverride);

      registrarAuditoria({
        transaccion: 'Anexos tarifarios',  // TODO: confirmar con equipo
        programa: 'Proveedores',               // TODO: confirmar con equipo
        tipoIdUsuario: aprobadorInfo?.userTypeId,
        numIdUsuario: aprobadorInfo?.userId,
        nombreUsuario: aprobadorInfo?.nombre,
        tipoIdAfiliado: aprobadorInfo?.userTypeId,
        numIdAfiliado: aprobadorInfo?.userId,
        ipUsuario: aprobadorInfo?.ip ?? '192.168.1.1',
        datosAdicionales: {
          Accion: 'Rechazo Anexo Tarifario',   // TODO: confirmar con equipo
          NumeroRadicado: 'N/A',
          Adjuntos: 'N/A',
          EmpresaCreada: 'N/A',
          ClienteCreado: 'N/A',
          ClienteVinculado: 'N/A',
          CiudadUsuario: ciudadUsuarioAuditoria,
          Rol: aprobadorInfo?.roles ?? 'N/A',
          Sede: aprobadorInfo?.sede ?? 'Sede no asociada',
          CorreoElectronico: correoOverride || aprobadorInfo?.correo,
          Resultado: resultado.exito ? 'exitoso' : 'no exitoso',
          MensajeError: resultado.mensaje ?? '',
        },
      });

      if (resultado.exito) {
        // Éxito - configurar resultado de rechazo
        const correoMostrar = resultado.correoDestino || correoOverride || aprobadorInfo?.correo || '';
        setCorreoDestino(correoMostrar);
        setTipoResultadoOperacion('rechazado');
        setEstadoNotificacionRespuesta(resultado.estadoNotificacion);
        setMensajeNotificacionRespuesta(resultado.mensajeNotificacion);
        
        setModalStep('exito');
      } else {
        // Error - mostrar modal de error con mensaje del servicio
        setModalStep('error');
        setMensajeError(resultado.mensaje || 'No fue posible rechazar el acuerdo tarifario. Por favor, inténtelo nuevamente.');
      }
    } catch (error) {
      setModalStep('error');
      setMensajeError('Ocurrió un error inesperado. Por favor, inténtelo nuevamente.');
    } finally {
      setProcesandoOperacion(false);
    }
  };

  const confirmarAprobacionFinal = async (correoOverride?: string) => {
    setProcesandoOperacion(true);

    try {
      const resultado = await actualizarEstadoAnexo("4", undefined, correoOverride);

      registrarAuditoria({
        transaccion: 'Anexos tarifarios',   // TODO: confirmar con equipo
        programa: 'Proveedores',                // TODO: confirmar con equipo
        tipoIdUsuario: aprobadorInfo?.userTypeId,
        numIdUsuario: aprobadorInfo?.userId,
        nombreUsuario: aprobadorInfo?.nombre,
        tipoIdAfiliado: aprobadorInfo?.userTypeId,
        numIdAfiliado: aprobadorInfo?.userId,
        ipUsuario: aprobadorInfo?.ip ?? '192.168.1.1',
        datosAdicionales: {
          Accion: 'Aprobación Anexo Tarifario', // TODO: confirmar con equipo
          NumeroRadicado: 'N/A',
          Adjuntos: 'N/A',
          EmpresaCreada: 'N/A',
          ClienteCreado: 'N/A',
          ClienteVinculado: 'N/A',
          CiudadUsuario: ciudadUsuarioAuditoria,
          Rol: aprobadorInfo?.roles ?? 'N/A',
          Sede: aprobadorInfo?.sede ?? 'Sede no asociada',
          CorreoElectronico: correoOverride || aprobadorInfo?.correo,
          Resultado: resultado.exito ? 'exitoso' : 'no exitoso',
          MensajeError: resultado.mensaje ?? '',
        },
      });

      if (resultado.exito) {
        // Éxito - configurar resultado de aprobación
        const correoMostrar = resultado.correoDestino || correoOverride || aprobadorInfo?.correo || '';
        setCorreoDestino(correoMostrar);
        setTipoResultadoOperacion('aprobado');
        setEstadoNotificacionRespuesta(resultado.estadoNotificacion);
        setMensajeNotificacionRespuesta(resultado.mensajeNotificacion);
        
        setModalStep('exito');
      } else {
        // Error - mostrar modal de error con mensaje del servicio
        setModalStep('error');
        setMensajeError(resultado.mensaje || 'No fue posible aprobar el acuerdo tarifario. Por favor, inténtelo nuevamente.');
      }
    } catch (error) {
      setModalStep('error');
      setMensajeError('Ocurrió un error inesperado. Por favor, inténtelo nuevamente.');
    } finally {
      setProcesandoOperacion(false);
    }
  };

  // Función para obtener el título del modal según el paso
  const getModalTitle = () => {
    switch (modalStep) {
      case 'clausulas':
        return 'Consultar clausulado del Acuerdo Tarifario';
      case 'confirmar-rechazo':
        return 'Confirmar rechazo del anexo';
      case 'confirmar-aprobacion':
        return 'Confirmar aprobación del anexo';
      case 'exito':
        return '';
      case 'error':
        return ''; // Sin título para errores
      default:
        return 'Consultar clausulado del Acuerdo Tarifario';
    }
  };

  // Función para obtener la variante del modal según el paso
  const getModalVariant = (): 'default' | 'clausulas' | 'compact' => {
    switch (modalStep) {
      case 'clausulas':
        return 'clausulas';
      case 'confirmar-rechazo':
      case 'confirmar-aprobacion':
      case 'exito':
      case 'error':
        return 'compact';
      default:
        return 'default';
    }
  };

  // Función para obtener el contenido del modal según el paso
  const getModalContent = () => {
    const anexoData = anexos.find(a => a.Id === anexoSeleccionado);
    
    switch (modalStep) {
      case 'clausulas':
        return (
          <ClausulasModalContent
            modalMessage={modalMessage}
            clausulasAceptadas={clausulasAceptadas}
            onClausulasChange={setClausulasAceptadas}
            onAprobar={aprobarAnexo}
            onRechazar={rechazarAnexo}
            onGestionarCups={gestionarPorCups}
          />
        );
      case 'confirmar-rechazo':
        return (
          <RechazoConfirmacionContent
            anexoNombre={anexoData?.Nombre || 'Anexo seleccionado'}
            tipo="rechazo"
            onCancelar={cancelarConfirmacion}
            onConfirmarRechazo={(motivo) => validarYContinuarConOperacion('rechazo', motivo)}
            procesando={procesandoOperacion}
          />
        );
      case 'confirmar-aprobacion':
        return (
          <RechazoConfirmacionContent
            anexoNombre={anexoData?.Nombre || 'Anexo seleccionado'}
            tipo="aprobacion"
            onCancelar={cancelarConfirmacion}
            onConfirmarAprobacion={() => validarYContinuarConOperacion('aprobacion')}
            procesando={procesandoOperacion}
          />
        );
      case 'exito':
        return (
          <ResultadoGestionModal
            tipo={tipoResultadoOperacion}
            correoDestino={correoDestino}
            estadoNotificacion={estadoNotificacionRespuesta}
            mensajeNotificacion={mensajeNotificacionRespuesta}
            onClose={finalizarOperacionExitosa}
          />
        );

      case 'error':
        return (
          <div style={{ 
            padding: '30px 25px 25px', 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '18px'
          }}>
            <div style={{ 
              width: '55px', 
              height: '55px', 
              backgroundColor: '#f44336', 
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '28px',
              fontWeight: 'bold'
            }}>
              ✕
            </div>
            <p style={{ 
              margin: 0, 
              fontSize: '15px', 
              color: '#333',
              lineHeight: '1.5',
              maxWidth: '450px'
            }}>
              {mensajeError}
            </p>
            <button
              onClick={cerrarModal}
              style={{
                marginTop: '8px',
                padding: '10px 35px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                minWidth: '110px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Aceptar
            </button>
          </div>
        );
      default:
        return null;
    }
  };


  const aprobarAnexo = () => {
    if (!clausulasAceptadas) return;
    // Cambiar al paso de confirmación de aprobación
    setModalStep('confirmar-aprobacion');
  };

  const rechazarAnexo = () => {
    if (!clausulasAceptadas) return;
    // Cambiar al paso de confirmación de rechazo
    setModalStep('confirmar-rechazo');
  };

  const gestionarPorCups = () => {
    if (!clausulasAceptadas) return;
    
    // Cerrar el modal y mostrar la sección de gestión de CUPS
    cerrarModal();
    setMostrarGestionCups(true);
  };


  // Estado para Excel generado dinámicamente
  const [excelAnexo, setExcelAnexo] = useState<null | { Base64: string; Extension: string; NomDoc: string }>(null);
  const [excelLoading, setExcelLoading] = useState(false);

  // Estado para controlar si el Excel de un anexo ya fue descargado (persistente en navegador, indexado por id de anexo)
  const [excelDescargado, setExcelDescargado] = useState(false);

  useEffect(() => {
    if (!anexoSeleccionado) {
      setExcelDescargado(false);
      return;
    }
    try {
      const descargados = JSON.parse(localStorage.getItem('excelAnexosDescargados') || '{}');
      setExcelDescargado(!!descargados[anexoSeleccionado]);
    } catch {
      setExcelDescargado(false);
    }
  }, [anexoSeleccionado]);

  const handleExcelDownload = () => {
    if (!excelAnexo || !excelAnexo.Base64 || !anexoSeleccionado) return;
    const link = document.createElement('a');
    link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${excelAnexo.Base64}`;
    link.download = excelAnexo.NomDoc || 'anexo_excel.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    try {
      const descargados = JSON.parse(localStorage.getItem('excelAnexosDescargados') || '{}');
      descargados[anexoSeleccionado] = true;
      localStorage.setItem('excelAnexosDescargados', JSON.stringify(descargados));
    } catch {
      // Si falla el guardado local, no bloqueamos la descarga.
    }
    setExcelDescargado(true);
  };

  // Efecto para obtener el Excel antes de renderizar la tabla
  useEffect(() => {
    const fetchExcel = async () => {
      setExcelAnexo(null);
      setExcelLoading(true);
      if (!anexoSeleccionado || !contratoSeleccionado || !userInfo) {
        setExcelLoading(false);
        return;
      }
      const contratoData = contratos.find(c => c.Id === contratoSeleccionado);
      const anexoData = anexos.find(a => a.Id === anexoSeleccionado);
      if (!contratoData || !anexoData) {
        setExcelLoading(false);
        return;
      }

      // Si estamos en desarrollo, no simular Excel
      if (__DEV__ || import.meta.env.MODE === 'development') {
        setExcelAnexo(null);
        setExcelLoading(false);
        return;
      }

      try {
        const requestBody = {
          Nit: userInfo.LegalID || userInfo.Nit || '',
          Contrato: contratoData.Nombre,
          Anexo: anexoData.Nombre,
          TipoDocumento: 'AT VERSION EXCEL'
        };
        const response = await fetch(
          '/_vti_bin/Servicios/ServicioTransar.svc/ObtenerDocumentoAnexoTarifario',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
          }
        );
        if (!response.ok) {
          setExcelLoading(false);
          return;
        }
        const data = await response.json();
        if (data.ResponseCode === 200 && data.Result && data.Result.Base64) {
          setExcelAnexo({
            Base64: data.Result.Base64,
            Extension: data.Result.Extension,
            NomDoc: data.Result.NomDoc
          });
        } else {
          setExcelAnexo(null);
        }
      } catch {
        setExcelAnexo(null);
      } finally {
        setExcelLoading(false);
      }
    };
    fetchExcel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anexoSeleccionado, contratoSeleccionado, userInfo]);

  const anexoSeleccionadoData = anexos.find(a => a.Id === anexoSeleccionado);

  return isAuthenticatedBoolean ? (
    <>
      <Spinner isVisible={loading} />
      <div className="header-section">
        <h1 className="main-title">Gestión de Anexos Tarifarios</h1>
        <p className="main-description">
          Revisa, aprueba o rechace los anexos tarifarios y gestione cada CUPS de forma individual.
        </p>
      </div>
      
      <div className="gap-40">
        <div className="contenedor activo">
          <div className="numbercontainer">
            <span className="digit">1</span>
          </div>
          <div className="form-container">
            <p style={{ marginBottom: '20px', color: '#333333ff', width: '100%' }}>
              Selecciona el contrato activo sobre el cual deseas consultar los anexos tarifarios.
            </p>

            {/* Mostrar fallback si no hay contratos disponibles */}
            {contratos.length === 0 && !loading && !error ? (
              <div className="no-data">
                <p>No tienes Anexos Tarifarios Activos Por revisar</p>
              </div>
            ) : (
              <>
                <div className="form-column" style={{ paddingTop: '40px' }}>
                  <div className="form-group">
                    <SelectDropdown
                      label="Contratos activos"
                      options={contratos.map(c => ({
                        value: c.Id,
                        label: c.Nombre,
                        data: c as unknown as Record<string, unknown>,
                      }))}
                      value={contratoSeleccionado ?? ''}
                      placeholder="Seleccione un contrato"
                      onChange={handleContratoChange}
                    />
                  </div>
                  <div className="form-group">
                    <SelectDropdown
                      label="Anexos tarifarios activos"
                      options={anexos.map(a => ({
                        value: a.Id,
                        label: a.Nombre,
                        data: a as unknown as Record<string, unknown>,
                      }))}
                      value={anexoSeleccionado ?? ''}
                      placeholder="Seleccione un anexo"
                      disabled={contratoSeleccionado === null}
                      onChange={handleAnexoChange}
                    />
                  </div>
                </div>

                {/* Tabla de datos del anexo - ahora dentro del mismo contenedor */}
                {mostrarTabla && anexoSeleccionadoData && !excelLoading && (
                  <div style={{ marginTop: '20px', width: '100%' }}>
                    <hr className="separador-resultados" style={{ 
                      border: 'none', 
                      borderTop: '3px dotted #e7e6e6', 
                      margin: '20px 0', 
                      width: '100%',
                      marginLeft: 'auto',
                      marginRight: 'auto'
                    }} />
                    
                    <p className="descripcion-anexo-resultados" style={{ 
                      marginBottom: '20px',
                    }}>
                      Aquí puedes visualizar el anexo y revisar las cláusulas para aprobarlas y habilitar la gestión de los CUPS.
                    </p>
                    
                    <div id="resultadoImagenesDiagnosticas" className="tabla-responsive-scroll">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Nombre del Anexo</th>
                            <th>Tipo de acuerdo</th>
                            <th>Fecha de inicio</th>
                            <th>Fecha fin</th>
                            <th>Descargar Excel</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{anexoSeleccionadoData.Nombre}</td>
                            <td>{anexoSeleccionadoData.TipoAcuerdo}</td>
                            <td>{anexoSeleccionadoData.FechaInicio}</td>
                            <td>{anexoSeleccionadoData.FechaFin}</td>
                            <td style={{ padding: 0, textAlign: 'center', verticalAlign: 'middle' }}>
                              <button
                                type="submit"
                                disabled={!excelAnexo || !excelAnexo.Base64 || excelDescargado}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  margin: 0,
                                  cursor: excelAnexo && excelAnexo.Base64 && !excelDescargado ? 'pointer' : 'not-allowed',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  opacity: excelAnexo && excelAnexo.Base64 && !excelDescargado ? 1 : 0.5
                                }}
                                onClick={e => {
                                  e.preventDefault();
                                  if (!excelAnexo || !excelAnexo.Base64 || excelDescargado) return;
                                  handleExcelDownload();
                                }}
                                title={excelDescargado ? 'Ya descargado' : (excelAnexo && excelAnexo.Base64 ? 'Descargar Excel' : 'No disponible')}
                              >
                                <i className="fa fa-download" style={{ fontSize: '22px', color: excelAnexo && excelAnexo.Base64 && !excelDescargado ? '#ff7300' : '#bbb', margin: 0, padding: 0, display: 'block' }}></i>
                              </button>
                            </td>
                            
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="botones" style={{ marginTop: '20px', justifyContent: 'center' }}>
                      <button
                        type="button"
                        className="anexos-tarifarios-busqueda-btn btn-visualizar"
                        onClick={DescargarAnexoPdf}
                        disabled={descargandoPdf}
                        style={{ marginRight: '10px', opacity: descargandoPdf ? 0.6 : 1, cursor: descargandoPdf ? 'not-allowed' : 'pointer' }}
                      >
                        <i className="fa fa-download" style={{ marginRight: '8px' }}></i>
                        {descargandoPdf ? 'Descargando...' : 'Descargar Anexo en PDF'}
                      </button>
                      {/* Solo mostrar el botón de consultar cláusulas si hay OtrosAspectos */}
                      {hasMeaningfulContent(anexoSeleccionadoData?.OtrosAspectos) && (
                        <button
                          type="button"
                          className="anexos-tarifarios-busqueda-btn btn-basic-opcion"
                          onClick={consultarClausulas}
                        >
                          <AnexoIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          Consultar Clausulado
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Panel de Gestión de CUPS */}
      {mostrarGestionCups && contratoSeleccionado && anexoSeleccionado && (
        <GestionCups
          contrato={contratos.find(c => c.Id === contratoSeleccionado) || null}
          anexo={anexos.find(a => a.Id === anexoSeleccionado) || null}
          onResetear={() => {
            // Resetear estados
            setContratoSeleccionado(null);
            setAnexoSeleccionado(null);
            setAnexos([]);
            setMostrarGestionCups(false);
            setMostrarTabla(false);
            // Recargar contratos
            if (userInfo) {
              fetchContratos(userInfo);
            }
          }}
        />
      )}
      
      <ContrastModeToggle />
      {/* Modal reutilizable */}
      <Modal
        isOpen={showModal}
        title={getModalTitle()}
        variant={getModalVariant()}
        onClose={cerrarModal}
        closeOnBackdropClick={false}
      >
        {getModalContent()}
      </Modal>

      {/* Modal para solicitar correo temporal si está vacío o es inválido */}
      <ModalCorreoTemporal
        isOpen={mostrarModalCorreoTemporal}
        correoInicial={correoTemporal}
        onConfirmar={procedeConCorreoTemporal}
        procesando={procesandoOperacion}
      />
 </>


  ) : (
    /* Vista de carga o no autenticado */
    <>
      <Spinner isVisible={true} text="Cargando información del usuario..." />
      <div style={{ textAlign: 'center', padding: '20px' }}></div>
    </>
  );
};

export default Busqueda;